/**
 * 创建群组按钮组件
 * 可复用的创建群组/添加成员按钮
 * @author AI Assistant
 * @date 2024/12/19
 */
import React, { useCallback, useMemo } from "react";
import { Tooltip, message as toast } from "antd";
import { useTranslation } from "react-i18next";
import IconFont from "@/components/icon";
import { ChatMessage, UID, ChatMessageType } from "@/types/chat";
import { checkCanAddOperate } from "@/utils/chat/group";
import useGroupUserSelector from "@/components/chat/GroupUserSelector";
import "./index.less";
import Context from "@/components/chat/context";
import ChatState from "@/types/chat/State";
import { connect } from "dva";
import {
  getSelectorSessions
} from "@/utils/chat/session";
import { openUserSelectionModal } from "@/utils/modals";
import {
  employeeToUid
} from "@/utils/chat/message";
import { SelectorMode } from "@jd/focus-desktop-comps/lib/es/UserSelection";
import debounce from "lodash/debounce";
import { get, isString } from "lodash";
import ImService from "@/server/ImService";
import { SessionSettingsType } from "@/types/chat/enum";

export interface CreateGroupButtonProps {
  /** 当前选中的会话 */
  // selectedSession: Session;
  /** 当前用户信息 */
  // currentEmployee: Employee;
  /** 群组成员身份 */
  // groupRosterIdentity?: GroupRosterIdentity | "";
  /** 群组成员列表 */
  // groupRosters?: any[];
  /** 会话成员列表 */
  // sessionMembers?: Partial<Employee>[];
  /** 用户数据 */
  // userData?: any;
  /** 转发消息 */
  // forwardMessages?: Function;
  /** 自定义区域额外内容 */
  customAreaExtra?: React.ReactNode;
  /** 回调函数 */
  callback?: Function;
  /** 是否显示按钮（覆盖默认权限检查） */
  visible?: boolean;
  /** 自定义样式类名 */
  className?: string;
  /** 自定义点击事件（覆盖默认行为） */
  onClick?: () => void;
  /** 自定义 clstag */
  clstag?: string;
  /** 按钮大小 */
  size?: "small" | "default" | "large";
  /** 是否禁用 */
  disabled?: boolean;
  customDisplayElement?: React.ReactNode;
  updateMessages: (data: { messages: ChatMessage[] }) => void;
  changeSelectMore: Function;
}

const CreateGroupButton = (props: CreateGroupButtonProps & ChatState) => {
  const {
    selectedSession,
    currentEmployee,
    sessionMembers = [],
    customAreaExtra,
    callback,
    visible,
    className = "",
    onClick,
    clstag,
    size = "default",
    disabled = false,
    customDisplayElement,
    allSessionList,
    messages,
    updateMessages,
    changeSelectMore
  } = props;
  const { t } = useTranslation("chat");
  const {
    groupRosters,
    groupRosterIdentity,
    userData
  } = React.useContext(Context);

  const exitAndMergeForward = async (data: any) => {
    const list = get(data, "data.result", []);
    if (!list || list.length <= 0) {
      return;
    }
    const imService = ImService.getInstance();
    const sessionId = selectedSession.sessionId;
    const optionResult: { gids: string[]; uids: UID[] } = { gids: [], uids: [] };
    const options = list.reduce((result: { gids: string[]; uids: UID[] }, item: any) => {
      if (item.isGroup) {
        result.gids.push(item.id);
      } else {
        result.uids.push({
          pin: item?.pin,
          app: item?.app,
          teamId: item?.teamId
        });
      }
      return result;
    }, optionResult);
    let mids: number[] = [];
    let joyspacePageIds: string[] = [];
    messages.forEach((message) => {
      if (message.type === ChatMessageType.JOYSPACEFILE) {
        joyspacePageIds.push();
      }
      mids.push(message.mid);
    });
    const result = await imService.mergeForwardMessage(sessionId, mids, options);
    if (isString(result) && result === SessionSettingsType.BANNED_POST) {
      return toast.warn(t("banned_post_tip"));
    }
    if (result) {
      toast.success(t("forwarding-successful"));
      updateMessages({ messages: [] });
      changeSelectMore({ selectMore: false });
    } else {
      toast.error(t("forwarding-failed"));
    }
  };
  const doExitAndMergeForward = debounce(async (data: any) => {
    return exitAndMergeForward(data);
  }, 500);
  const forwardMessages = () => {
    // 合并转发
    const fixedUsers = getSelectorSessions(allSessionList, currentEmployee);
    openUserSelectionModal(
      {
        title: t("select-session-contacts"),
        fixedUsers: fixedUsers,
        frozenIds: [],
        currentUser: employeeToUid(currentEmployee),
        mode: SelectorMode.forward
      },
      (data, close) => {
        doExitAndMergeForward(data);
        close();
      },
      userData
    );

    // 清除信息
    updateMessages({ messages: [] });
    changeSelectMore({ selectMore: false, type: null });
  };

  // 使用 useGroupUserSelector hook
  const openUserSelector = useGroupUserSelector({
    selectedSession,
    currentEmployee,
    groupRosters,
    sessionMembers,
    userData,
    callback,
    forwardMessages,
    customAreaExtra
  });

  // 检查是否可以添加操作
  const canAddOperate = useMemo(() => {
    if (visible !== undefined) {
      return visible;
    }
    return checkCanAddOperate({
      selectedSession,
      currentEmployee,
      groupRosterIdentity
    });
  }, [visible, selectedSession, currentEmployee, groupRosterIdentity]);


  // 生成按钮标题
  const title = useMemo(() => {
    return selectedSession.isGroup ? t("add_member") : t("create_group");
  }, [selectedSession.isGroup, t]);

  // 处理点击事件
  const handleClick = useCallback(() => {
    if (disabled) return;
    
    if (onClick) {
      onClick();
    } else {
      openUserSelector();
    }
  }, [disabled, onClick, openUserSelector]);

  // 如果不可见，返回 null
  if (!canAddOperate) {
    return null;
  }

  const sizeClass = size !== "default" ? `create-group-btn--${size}` : "";
  const disabledClass = disabled ? "create-group-btn--disabled" : "";

  return (
    customDisplayElement ? <div onClick={handleClick}>{customDisplayElement}</div> : (<Tooltip title={title} overlayStyle={{ whiteSpace: "nowrap" }} placement="bottom">
      <div className="add_user no-draggable" onClick={handleClick} clstag={clstag}>
        <IconFont type="iconchuangjianqunzu" />
      </div>
    </Tooltip>)
  );
};

function mapStateToProps({ chat, netStatus }: any) {
  const {
    selectedSession,
    currentEmployee,
    sessionMembers,
    allSessionList,
    messages,
    sessionMessageMap
  } = chat as ChatState;
  const sessionMessages: ChatMessage[] = sessionMessageMap[selectedSession.sessionId] || [];
  const { newPublicResult, newPrivateResult } = netStatus;
  return {
    selectedSession,
    currentEmployee,
    sessionMembers,
    allSessionList,
    messages,
    newPublicResult,
    newPrivateResult,
    sessionMessages
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateMessages(data: { messages: ChatMessage[] }) {
      dispatch({ type: "chat/updateMessages", payload: data });
    },
    changeSelectMore(data: { selectMore: boolean }) {
      dispatch({ type: "chat/changeSelectMore", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateGroupButton);
